import React, { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Check, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { SORTABLE_KEYS } from "@/lib/watchlistSortFilter";

// Human labels for each sortable key. Keys map onto the SORT_KEYS
// extractor in /lib/watchlistSortFilter.js so the labels here are the
// single source of truth for the mobile Sort dropdown UI.
const SORT_LABELS = {
  added_at:               { t_key: "watchlist.sort.recently_added",       fallback: "Recently added" },
  ticker:                 { t_key: "watchlist.sort.ticker",               fallback: "Ticker" },
  name:                   { t_key: "watchlist.sort.name",                 fallback: "Name" },
  price:                  { t_key: "watchlist.sort.price",                fallback: "Price" },
  fair_value:             { t_key: "watchlist.sort.fair_value",           fallback: "Fair value" },
  upside_pct:             { t_key: "watchlist.sort.upside",               fallback: "Upside" },
  ai_score:               { t_key: "watchlist.sort.ai_score",             fallback: "AI Score" },
  status:                 { t_key: "watchlist.sort.status",               fallback: "Status" },
  market_data_updated_at: { t_key: "watchlist.sort.last_updated",         fallback: "Last updated" },
};

/**
 * WatchlistSortButton (Phase 8.WLNAV.v1)
 * ─────────────────────────────────────
 * Compact "Sort" button that opens a popover with the full list of
 * sortable columns. Used mainly on mobile where the table header is
 * hidden — but is exposed on desktop too for parity.
 *
 * The parent owns the sort state:
 *   { key: string, dir: "asc" | "desc" }
 */
export default function WatchlistSortButton({ sort, onChange, onReset, isDefault, t }) {
  const [open, setOpen] = useState(false);
  const tr = (k, fb) => {
    const v = t(k);
    return v && v !== k ? v : fb;
  };

  const activeLabel = SORT_LABELS[sort?.key] ? tr(SORT_LABELS[sort.key].t_key, SORT_LABELS[sort.key].fallback) : tr("watchlist.sort.button", "Sort");
  const activeDirGlyph = sort?.dir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;

  const pick = (key) => {
    // Clicking the currently active key toggles direction; otherwise
    // reset to a sensible default per column (numbers → desc, strings → asc).
    const nextDir = sort?.key === key
      ? (sort.dir === "asc" ? "desc" : "asc")
      : (["ticker", "name", "status"].includes(key) ? "asc" : "desc");
    onChange({ key, dir: nextDir });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid="watchlist-sort-button"
          className="h-9 whitespace-nowrap"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          <span className="text-xs">{tr("watchlist.sort.button", "Sort")}:</span>
          <span className="ml-1 text-xs font-medium inline-flex items-center">{activeLabel}{activeDirGlyph}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-1"
        data-testid="watchlist-sort-popover"
      >
        <div className="py-1 px-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          {tr("watchlist.sort.header", "Sort by")}
        </div>
        <ul role="listbox" aria-label="Sort by">
          {SORTABLE_KEYS.map((k) => {
            const label = tr(SORT_LABELS[k].t_key, SORT_LABELS[k].fallback);
            const active = sort?.key === k;
            return (
              <li key={k} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => pick(k)}
                  data-testid={`watchlist-sort-option-${k}`}
                  className={`w-full text-left px-2 py-1.5 rounded-sm text-sm flex items-center justify-between ${active ? "bg-secondary/70" : "hover:bg-secondary/40"}`}
                >
                  <span>{label}</span>
                  {active ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      {sort.dir === "asc"
                        ? <>{tr("watchlist.sort.asc", "Asc")}<ArrowUp className="w-3 h-3" /></>
                        : <>{tr("watchlist.sort.desc", "Desc")}<ArrowDown className="w-3 h-3" /></>
                      }
                      <Check className="w-3 h-3 text-[#3FAE89] ml-1" />
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
        {/* Phase 8.WLNAV.v1.2 — subtle "Reset to default" inside the
            popover. Clears the per-user saved preference and returns to
            "Most recently added first". Disabled when already at the
            default so users get clear feedback that nothing needs
            resetting. Kept intentionally small — not a standalone
            button in the toolbar. */}
        {onReset ? (
          <div className="mt-1 pt-1 border-t border-border/50">
            <button
              type="button"
              onClick={() => { onReset(); setOpen(false); }}
              disabled={!!isDefault}
              data-testid="watchlist-sort-reset-default"
              className="w-full text-left px-2 py-1.5 rounded-sm text-[11px] flex items-center gap-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary/40 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
            >
              <RotateCcw className="w-3 h-3" />
              {tr("watchlist.sort.reset_default", "Reset to default")}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * SortableHeader — clickable table header cell that renders the current
 * sort state (asc/desc/none) via arrow glyphs. Emits `onSort(key)` where
 * the parent decides how to update state (usually via `pick(key)` above).
 */
export function SortableHeader({ children, sortKey, sort, onSort, align = "left", testId }) {
  const active = sort?.key === sortKey;
  const glyph = !active
    ? <ArrowUpDown className="w-3 h-3 opacity-50" />
    : sort.dir === "asc"
      ? <ArrowUp className="w-3 h-3 text-[#3FAE89]" />
      : <ArrowDown className="w-3 h-3 text-[#3FAE89]" />;

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      data-testid={testId || `watchlist-header-sort-${sortKey}`}
      aria-pressed={active}
      className={`inline-flex items-center gap-1 select-none cursor-pointer hover:text-foreground/90 transition-colors ${align === "right" ? "flex-row-reverse" : ""}`}
    >
      <span>{children}</span>
      {glyph}
    </button>
  );
}
